import React, { useState, useEffect } from 'react';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { addDoc, collection, updateDoc, deleteDoc, doc, getDocs, getDoc } from 'firebase/firestore';
import { db, auth, storage } from '../firebase-config';
import { useDropzone } from 'react-dropzone';
import {
  Button,
  TextField,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Alert,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Save as SaveIcon, Cancel as CancelIcon, Visibility as VisibilityIcon, CloudDownload as CloudDownloadIcon, CloudUpload as CloudUploadIcon} from '@mui/icons-material';

const KnowledgePackCreator = () => {
  const [knowledgePacks, setKnowledgePacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingPack, setEditingPack] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formData, setFormData] = useState({ title: '', description: '' });
  const [files, setFiles] = useState([]);
  const [packFiles, setPackFiles] = useState([]); // To hold files in the selected pack
  const [uploading, setUploading] = useState(false);
  const [isFileListVisible, setIsFileListVisible] = useState(false); // Toggle file list visibility
  const [thumbnail, setThumbnail] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(''); // To preview the thumbnail image
  

  // Fetch all knowledge packs for the current user
  const fetchKnowledgePacks = async () => {
    setLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, 'users', auth.currentUser.uid, 'knowledgePacks'));
      const packs = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setKnowledgePacks(packs);
      setLoading(false);
    } catch (err) {
      setError('Failed to load knowledge packs.');
      setLoading(false);
    }
  };

  useEffect(() => {
    if (auth.currentUser) {
      fetchKnowledgePacks();
    }
  }, [auth.currentUser]);

  // Handle opening/closing the form
  const toggleForm = (pack = null) => {
    setIsFormOpen(!isFormOpen);
    if (pack) {
      setFormData({ title: pack.title, description: pack.description });
      setEditingPack(pack.id);
    } else {
      setFormData({ title: '', description: '' });
      setEditingPack(null);
      setFiles([]);
    }
  };

  // Fetch files of a selected knowledge pack
  const fetchPackFiles = async (packId) => {
    try {
      const packRef = doc(db, 'users', auth.currentUser.uid, 'knowledgePacks', packId);
      const packSnap = await getDoc(packRef);
      if (packSnap.exists()) {
        setPackFiles(packSnap.data().files || []);
        setIsFileListVisible(true);
      }
    } catch (err) {
      setError('Failed to load knowledge pack files.');
    }
  };

  // Handle file viewing or downloading
  const handleFileAction = (file, action) => {
    if (action === 'preview') {
      window.open(file.url, '_blank'); // Preview the file in a new tab
    } else if (action === 'download') {
      const link = document.createElement('a');
      link.href = file.url;
      link.download = file.name;
      link.click(); // Download the file
    }
  };

  // Handle saving the knowledge pack and uploading files
  // Handle saving the knowledge pack and uploading files + thumbnail
  const handleSavePack = async () => {
    if (!formData.title || !formData.description) {
      alert('Please fill out all fields.');
      return;
    }

    setUploading(true);

    try {
      let packId = editingPack;

      // Step 1: Create or update a knowledge pack in the user's collection
      if (!editingPack) {
        const knowledgePackDocRef = await addDoc(collection(db, 'users', auth.currentUser.uid, 'knowledgePacks'), {
          title: formData.title,
          description: formData.description,
          ownerId: auth.currentUser.uid,
          createdAt: new Date().toISOString(),
        });
        packId = knowledgePackDocRef.id;
      } else {
        const docRef = doc(db, 'users', auth.currentUser.uid, 'knowledgePacks', packId);
        await updateDoc(docRef, { title: formData.title, description: formData.description });
      }

      // Step 2: Upload thumbnail image if present
      let thumbnailURL = '';
      if (thumbnail) {
        const thumbnailRef = ref(storage, `knowledgePacks/${auth.currentUser.uid}/${packId}/thumbnail_${thumbnail.name}`);
        const thumbnailSnapshot = await uploadBytes(thumbnailRef, thumbnail);
        thumbnailURL = await getDownloadURL(thumbnailSnapshot.ref);

        // Update the knowledge pack document with the thumbnail URL
        await updateDoc(doc(db, 'users', auth.currentUser.uid, 'knowledgePacks', packId), { thumbnailURL });
      }

      // Step 3: Upload the selected files
      const uploadedFilesData = await Promise.all(files.map((file) => uploadFileToStorage(file, packId)));
      if (uploadedFilesData.length > 0) {
        await updateDoc(doc(db, 'users', auth.currentUser.uid, 'knowledgePacks', packId), { files: uploadedFilesData });
      }

      setIsFormOpen(false);
      setFiles([]);
      setThumbnail(null);
      setThumbnailPreview('');
      setUploading(false);

      // Automatically refresh the table of knowledge packs
      await fetchKnowledgePacks();
    } catch (err) {
      setError('Failed to save knowledge pack.');
      setUploading(false);
    }
  };


  // Function to upload each file to Firebase Storage
  const uploadFileToStorage = async (file, packId) => {
    const storageRef = ref(storage, `knowledgePacks/${auth.currentUser.uid}/${packId}/${file.name}`);
    const snapshot = await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);
    return { name: file.name, path: snapshot.ref.fullPath, url: downloadURL };
  };

  // Drag-and-Drop setup using `react-dropzone` for thumbnail
  const { getRootProps: getThumbnailRootProps, getInputProps: getThumbnailInputProps, isDragActive: isThumbnailDragActive } = useDropzone({
    accept: 'image/*',
    onDrop: (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (file) {
        setThumbnail(file);
        setThumbnailPreview(URL.createObjectURL(file)); // Preview the thumbnail
      }
    },
  });


  // Drag-and-Drop setup using `react-dropzone`
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles) => {
      setFiles((prevFiles) => [...prevFiles, ...acceptedFiles]);
    },
  });

  // Handle deleting a knowledge pack
  const handleDeletePack = async (packId) => {
    try {
      // Delete files from Firebase Storage
      const packFiles = knowledgePacks.find(pack => pack.id === packId).files || [];
      const deletePromises = packFiles.map(file => {
        const fileRef = ref(storage, file.path);
        return deleteObject(fileRef);
      });

      await Promise.all(deletePromises);

      // Delete knowledge pack from Firestore
      await deleteDoc(doc(db, 'users', auth.currentUser.uid, 'knowledgePacks', packId));

      // Remove from local state
      setKnowledgePacks(knowledgePacks.filter(pack => pack.id !== packId));
    } catch (err) {
      setError('Failed to delete knowledge pack.');
    }
  };

  // Function to share knowledge pack to the global knowledge store
  const handleShareToGlobalStore = async (packId) => {
    try {
      // Fetch the user's knowledge pack data
      const userPackRef = doc(db, 'users', auth.currentUser.uid, 'knowledgePacks', packId);
      const userPackSnap = await getDoc(userPackRef);

      if (userPackSnap.exists()) {
        const knowledgePackData = userPackSnap.data();

        // Copy the knowledge pack to the global store
        await addDoc(collection(db, 'globalKnowledgeStore'), {
          ...knowledgePackData,
          sharedBy: auth.currentUser.uid, // Add user ID of the sharer
          sharedAt: new Date().toISOString(), // Timestamp for when the pack was shared
        });

        alert('Knowledge Pack shared to the global store!');
      } else {
        console.error('No such document!');
      }
    } catch (error) {
      console.error('Error sharing knowledge pack:', error);
      alert('Failed to share knowledge pack.');
    }
  };


  return (
    <div style={{ padding: '20px' }}>
      <Typography variant="h4" gutterBottom>
        Knowledge Pack Management
      </Typography>

      {loading && <CircularProgress />}
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

      {/* Table displaying knowledge packs */}
      {!loading && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Owner</TableCell>
                <TableCell>Created At</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {knowledgePacks.map((pack) => (
                <TableRow key={pack.id}>
                  <TableCell>{pack.title}</TableCell>
                  <TableCell>{pack.description}</TableCell>
                  <TableCell>{pack.ownerId}</TableCell>
                  <TableCell>{new Date(pack.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Tooltip title="View Files">
                      <IconButton onClick={() => fetchPackFiles(pack.id)}>
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton onClick={() => toggleForm(pack)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton onClick={() => handleDeletePack(pack.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Share to Global Store">
                      <IconButton onClick={() => handleShareToGlobalStore(pack.id)}>
                        <CloudUploadIcon /> {/* This icon can be changed if needed */}
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Button
        variant="contained"
        color="primary"
        startIcon={<AddIcon />}
        style={{ marginTop: '20px' }}
        onClick={() => toggleForm()}
      >
        Create New Knowledge Pack
      </Button>

      {/* File List Modal */}
      {isFileListVisible && (
        <Dialog open={isFileListVisible} onClose={() => setIsFileListVisible(false)} fullWidth maxWidth="sm">
          <DialogTitle>Files in Knowledge Pack</DialogTitle>
          <DialogContent>
            <List>
              {packFiles.map((file, index) => (
                <ListItem key={index}>
                  <ListItemText primary={file.name} />
                  <IconButton onClick={() => handleFileAction(file, 'preview')} title="Preview">
                    <VisibilityIcon />
                  </IconButton>
                  <IconButton onClick={() => handleFileAction(file, 'download')} title="Download">
                    <CloudDownloadIcon />
                  </IconButton>
                </ListItem>
              ))}
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsFileListVisible(false)} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Dialog for creating/editing a knowledge pack */}
      <Dialog open={isFormOpen} onClose={() => setIsFormOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editingPack ? 'Edit Knowledge Pack' : 'Create Knowledge Pack'}</DialogTitle>
        <DialogContent>
          <TextField
            label="Title"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            fullWidth
            margin="normal"
            multiline
          />

          {/* Drag-and-Drop for thumbnail image */}
          {/*<Typography variant="h7">Upload Thumbnail</Typography>*/}
          <Paper
            {...getThumbnailRootProps()}
            style={{
              padding: '20px',
              marginBottom: '20px',
              border: '2px dashed #ccc',
              textAlign: 'center',
              backgroundColor: isThumbnailDragActive ? '#e0f7fa' : 'transparent',
            }}
          >
            <input {...getThumbnailInputProps()} />
            <Typography variant="body2">
              {isThumbnailDragActive ? 'Drop the image here ...' : 'Drag and drop thumbnail image here, or click to select an image'}
            </Typography>
          </Paper>
          {thumbnailPreview && (
            <div style={{ marginBottom: '20px' }}>
              <Typography variant="body2">Thumbnail Preview:</Typography>
              <img src={thumbnailPreview} alt="Thumbnail preview" style={{ width: '100%', maxHeight: '200px', objectFit: 'contain' }} />
            </div>
          )}

          {/* Drag-and-Drop for files */}
          <Paper
            {...getRootProps()}
            style={{
              padding: '20px',
              marginBottom: '20px',
              border: '2px dashed #ccc',
              textAlign: 'center',
              backgroundColor: isDragActive ? '#e0f7fa' : 'transparent',
            }}
          >
            <input {...getInputProps()} />
            <Typography variant="body2">
              {isDragActive ? 'Drop the files here ...' : 'Drag and drop files here, or click to select files'}
            </Typography>
          </Paper>

          {files.length > 0 && (
            <List>
              {files.map((file, index) => (
                <ListItem key={index}>
                  <ListItemText primary={file.name} />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsFormOpen(false)} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button onClick={handleSavePack} startIcon={<SaveIcon />} color="primary" disabled={uploading}>
            {uploading ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default KnowledgePackCreator;
